import type { ReactNode } from "react";

/* ---- category chip styles ---- */
const solidMap: Record<string, string> = {
  反转: "bg-primary-500 text-background-50",
  打脸: "bg-accent-500 text-foreground-950",
  辟谣: "bg-secondary-500 text-background-50",
  实锤: "bg-foreground-950 text-background-50",
  追踪: "bg-background-700 text-background-50",
  真假: "bg-primary-600 text-background-50",
  后续: "bg-secondary-600 text-background-50",
};

const softMap: Record<string, string> = {
  反转: "bg-primary-100 text-primary-800 border-primary-200",
  打脸: "bg-accent-100 text-accent-900 border-accent-200",
  辟谣: "bg-secondary-100 text-secondary-900 border-secondary-200",
  实锤: "bg-foreground-100 text-foreground-800 border-foreground-200",
  追踪: "bg-background-100 text-foreground-700 border-background-300",
  真假: "bg-primary-50 text-primary-800 border-primary-100",
  后续: "bg-secondary-100 text-secondary-900 border-secondary-200",
};

export function catSolid(cat: string): string {
  return solidMap[cat] ?? "bg-background-700 text-background-50";
}

export function catSoft(cat: string): string {
  return softMap[cat] ?? "bg-secondary-100 text-secondary-900 border-secondary-200";
}

export function CatSolid({ cat }: { cat: string }) {
  return (
    <span
      className={`inline-flex items-center whitespace-nowrap rounded px-2 py-0.5 text-xs font-semibold ${catSolid(cat)}`}
    >
      {cat}
    </span>
  );
}

export function CatSoft({ cat }: { cat: string }) {
  return (
    <span
      className={`inline-flex items-center whitespace-nowrap rounded-full border px-2.5 py-0.5 text-xs font-medium ${catSoft(cat)}`}
    >
      {cat}
    </span>
  );
}

/* ---- section heading ---- */
interface SectionHeadProps {
  id?: string;
  kicker: string;
  title: string;
  subtitle?: string;
  linkText?: string;
  align?: "left" | "center";
}

export function SectionHead({
  id,
  kicker,
  title,
  subtitle,
  linkText,
  align = "left",
}: SectionHeadProps) {
  return (
    <div
      id={id}
      className={`mb-6 flex flex-col gap-4 sm:flex-row sm:items-end ${
        align === "center" ? "sm:flex-col sm:items-center sm:text-center" : "sm:justify-between"
      }`}
    >
      <div className={align === "center" ? "text-center" : ""}>
        <div className="mb-2 flex items-center gap-2">
          <span className="h-2 w-2 rounded-full bg-primary-500" />
          <span className="text-xs font-semibold uppercase tracking-[0.18em] text-primary-700">
            {kicker}
          </span>
        </div>
        <h2 className="text-2xl font-extrabold tracking-tight text-foreground-950 md:text-[28px]">
          {title}
        </h2>
        {subtitle ? (
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-600">{subtitle}</p>
        ) : null}
      </div>
      {linkText ? (
        <a
          href="#top"
          className="group inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 py-1.5 text-xs font-medium text-foreground-700 transition hover:border-primary-300 hover:text-primary-700"
        >
          {linkText}
          <i className="ri-arrow-right-line transition group-hover:translate-x-0.5" />
        </a>
      ) : null}
    </div>
  );
}

/* ---- heat chip ---- */
export function Heat({ value }: { value: string }) {
  return (
    <span className="inline-flex items-center gap-1 whitespace-nowrap text-xs font-medium text-accent-700">
      <i className="ri-fire-line text-accent-500" />
      {value}
    </span>
  );
}

/* ---- shell wrapper ---- */
export function Shell({ children }: { children: ReactNode }) {
  return <div className="mx-auto w-full max-w-[1280px] px-4 md:px-6">{children}</div>;
}